import { toJalaliFullDate } from "../../../middleware/middleware";
import { formatUpdateTypeLabel } from "../../../pages/SLN/solarent-operator/all-requests/allRequestsPageUtils";
import {
  getAttachmentUrl,
  getAuthorName,
  getEntityId,
  getStatusLabel as getMehrsanStatusLabel,
  normalizeTimelineLogs,
} from "./mehrsanOperationsUtils";
import { getLoanStatusLabel } from "./lendingOperationsUtils";
import {
  PAYMENT_METHOD_LEGACY,
  PAYMENT_METHOD_LOAN,
} from "./financialCoreConstants";
import {
  formatMoney,
  getPaymentMethodLabel,
  getTransactionStatusLabel,
} from "./financialCoreUtils";

export const ACTIVITY_CATEGORIES = {
  WORKFLOW: "workflow",
  FOLLOW_UP: "followup",
  MEHRSAN: "mehrsan",
  LENDING: "lending",
  ASSIGNMENT: "assignment",
  INTERNAL_NOTE: "internal_note",
  FINANCIAL: "financial",
};

const SOURCE_LABELS = {
  [ACTIVITY_CATEGORIES.WORKFLOW]: "گردش کار",
  [ACTIVITY_CATEGORIES.FOLLOW_UP]: "پیگیری",
  [ACTIVITY_CATEGORIES.MEHRSAN]: "مهرسان",
  [ACTIVITY_CATEGORIES.LENDING]: "وام",
  [ACTIVITY_CATEGORIES.ASSIGNMENT]: "مسئولیت",
  [ACTIVITY_CATEGORIES.INTERNAL_NOTE]: "یادداشت داخلی",
  [ACTIVITY_CATEGORIES.FINANCIAL]: "مالی",
};

/** ترتیب پایدار وقتی تاریخ برابر است (عدد کمتر = بالاتر در لیست نزولی) */
const SOURCE_PRIORITY = {
  [ACTIVITY_CATEGORIES.MEHRSAN]: 1,
  [ACTIVITY_CATEGORIES.FINANCIAL]: 2,
  [ACTIVITY_CATEGORIES.LENDING]: 3,
  [ACTIVITY_CATEGORIES.FOLLOW_UP]: 4,
  [ACTIVITY_CATEGORIES.INTERNAL_NOTE]: 5,
  [ACTIVITY_CATEGORIES.ASSIGNMENT]: 6,
  [ACTIVITY_CATEGORIES.WORKFLOW]: 7,
};

const PIN_CHANGE_THRESHOLD_MS = 2000;

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

/** تاریخ نامعتبر/خالی همیشه پایین لیست (مرتب‌سازی نزولی) */
export const NO_DATE_MS = -1;

const UPDATE_TYPE_NUM = {
  Assigned: 1,
  Rejected: 2,
  Approved: 3,
  Edited: 4,
};

export const resolveUpdateTypeNum = (value) => {
  const n = Number(value);
  if (!Number.isNaN(n) && n > 0) return n;
  if (typeof value === "string" && UPDATE_TYPE_NUM[value] != null) {
    return UPDATE_TYPE_NUM[value];
  }
  return NaN;
};

const parseTime = (value) => {
  if (value == null || value === "") return NO_DATE_MS;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? NO_DATE_MS : t;
};

const makeActivity = ({
  id,
  category,
  activityType,
  title,
  description = "",
  occurredAt,
  authorName = "",
  attachmentUrl = null,
  chipColor = "default",
  sortTie = 0,
}) => ({
  id,
  category,
  activityType,
  sourceLabel: SOURCE_LABELS[category] || category,
  title: title || "—",
  description: description?.trim() || "",
  occurredAt,
  occurredAtMs: parseTime(occurredAt),
  authorName: getAuthorName(authorName),
  attachmentUrl: attachmentUrl || null,
  chipColor,
  sourcePriority: SOURCE_PRIORITY[category] ?? 99,
  sortTie,
});

const inferAssignmentFromHistory = (item) => {
  const desc = String(pick(item, "description", "Description") || "");
  if (desc.includes("آزاد شد") || desc.includes("آزادسازی")) return "release";
  if (desc.includes("دریافت شد")) return "claim";
  if (
    pick(item, "toUserId", "ToUserId") != null ||
    pick(item, "fromUserId", "FromUserId") != null
  ) {
    return "reassign";
  }
  return "assign";
};

const workflowChipColor = (updateType, description) => {
  const type = resolveUpdateTypeNum(updateType);
  const desc = String(description || "");
  if (type === 2 || desc.includes("رد")) return "error";
  if (type === 3 || desc.includes("تایید")) return "success";
  if (desc.includes("برگشت") || desc.includes("عقب")) return "warning";
  return "primary";
};

export const normalizeWorkflowActivities = (history = []) => {
  if (!Array.isArray(history)) return [];

  return history.map((item, index) => {
    const updateType = pick(item, "updateType", "UpdateType");
    const description = pick(item, "description", "Description") || "";
    const typeNum = resolveUpdateTypeNum(updateType);

    if (typeNum === 1) {
      const assignKind = inferAssignmentFromHistory(item);
      const titles = {
        claim: "دریافت درخواست",
        release: "آزادسازی درخواست",
        reassign: "تغییر مسئول",
        assign: "ارجاع / تخصیص",
      };
      return makeActivity({
        id: `assignment-hist-${getEntityId(item) ?? index}`,
        category: ACTIVITY_CATEGORIES.ASSIGNMENT,
        activityType: `assignment_${assignKind}`,
        title: titles[assignKind] || titles.assign,
        description,
        occurredAt: pick(item, "createOn", "CreateOn"),
        authorName: pick(item, "fullName", "FullName"),
        chipColor:
          assignKind === "release"
            ? "warning"
            : assignKind === "claim"
            ? "success"
            : "primary",
        sortTie: index,
      });
    }

    let activityType = "workflow_generic";
    let title = formatUpdateTypeLabel(updateType);

    if (typeNum === 2) {
      activityType = "workflow_reject";
      title = "رد درخواست";
    } else if (typeNum === 3) {
      activityType = "workflow_approve";
      title = "تایید درخواست";
    } else if (typeNum === 4) {
      activityType = "workflow_edit";
      title = "ویرایش پرونده";
      if (description.includes("مرحله")) {
        activityType = "workflow_step";
        title = "تغییر مرحله";
      }
    }

    return makeActivity({
      id: `workflow-hist-${getEntityId(item) ?? index}`,
      category: ACTIVITY_CATEGORIES.WORKFLOW,
      activityType,
      title,
      description,
      occurredAt: pick(item, "createOn", "CreateOn"),
      authorName: pick(item, "fullName", "FullName"),
      chipColor: workflowChipColor(updateType, description),
      sortTie: index,
    });
  });
};

export const normalizeFollowUpActivities = (followUps = []) => {
  if (!Array.isArray(followUps)) return [];

  return followUps.map((item, index) => {
    const typeTitle = pick(item, "followUpTypeTitle", "FollowUpTypeTitle") || "پیگیری";
    const callResult = pick(item, "callResultTitle", "CallResultTitle");
    const note = pick(item, "note", "Note") || "";
    const nextDate = pick(item, "nextFollowUpDate", "NextFollowUpDate");

    let chipColor = "primary";
    if (callResult && String(callResult).includes("نیاز")) chipColor = "warning";
    if (callResult && String(callResult).includes("آماده")) chipColor = "success";

    const descParts = [note];
    if (callResult) descParts.unshift(`نتیجه: ${callResult}`);
    if (nextDate) {
      const jalaliNext = toJalaliFullDate(nextDate);
      descParts.push(`پیگیری بعدی: ${jalaliNext || nextDate}`);
    }

    return makeActivity({
      id: `followup-${getEntityId(item) ?? index}`,
      category: ACTIVITY_CATEGORIES.FOLLOW_UP,
      activityType: "followup_entry",
      title: typeTitle,
      description: descParts.filter(Boolean).join("\n"),
      occurredAt: pick(item, "createOn", "CreateOn"),
      authorName: pick(item, "createdByFullName", "CreatedByFullName"),
      chipColor,
      sortTie: index,
    });
  });
};

const mehrsanLogChipColor = (status) => {
  const s = Number(status);
  if (s === 4) return "success";
  if (s === 2 || s === 3) return "warning";
  if (s === 5) return "error";
  return "primary";
};

export const normalizeMehrsanActivities = (processesWithLogs = []) => {
  const activities = [];
  let tie = 0;

  (Array.isArray(processesWithLogs) ? processesWithLogs : []).forEach((proc) => {
    const processId = getEntityId(proc);
    const typeTitle =
      pick(proc, "processTypeTitle", "ProcessTypeTitle") ||
      pick(proc, "title", "Title") ||
      "فرآیند مهرسان";

    const logs = normalizeTimelineLogs(proc?.logs || proc?.Logs || []);
    logs.forEach((log) => {
      const logId = getEntityId(log);
      const status = pick(log, "status", "Status");
      const statusTitle = getMehrsanStatusLabel(
        status,
        pick(log, "statusTitle", "StatusTitle")
      );
      const actionTitle = pick(log, "actionTitle", "ActionTitle") || "اقدام مهرسان";
      const attachmentUrl = getAttachmentUrl(log);

      let activityType = "mehrsan_log";
      let title = actionTitle;
      if (actionTitle.includes("مهاجرت")) activityType = "mehrsan_migration";
      if (actionTitle.includes("بارگذاری") || attachmentUrl) {
        activityType = "mehrsan_attachment";
        title = attachmentUrl ? "پیوست / قرارداد مهرسان" : actionTitle;
      }
      if (statusTitle) title = `${actionTitle} — ${statusTitle}`;

      const descParts = [
        pick(log, "description", "Description"),
        typeTitle ? `نوع فرآیند: ${typeTitle}` : "",
        pick(log, "referenceNumber", "ReferenceNumber")
          ? `مرجع: ${pick(log, "referenceNumber", "ReferenceNumber")}`
          : "",
      ].filter(Boolean);

      activities.push(
        makeActivity({
          id: `mehrsan-${processId}-log-${logId ?? `n${tie}`}`,
          category: ACTIVITY_CATEGORIES.MEHRSAN,
          activityType,
          title,
          description: descParts.join("\n"),
          occurredAt: pick(log, "createOn", "CreateOn"),
          authorName: pick(log, "createdByFullName", "CreatedByFullName"),
          attachmentUrl,
          chipColor: mehrsanLogChipColor(status),
          sortTie: tie++,
        })
      );
    });
  });

  return activities;
};

const lendingLogChipColor = (status) => {
  const s = Number(status);
  if (s === 5 || s === 7 || s === 8) return "success";
  if (s === 2 || s === 3) return "warning";
  if (s === 4 || s === 6 || s === 9) return "error";
  return "primary";
};

export const normalizeLendingActivities = (processesWithLogs = []) => {
  const activities = [];
  let tie = 0;

  (Array.isArray(processesWithLogs) ? processesWithLogs : []).forEach((proc) => {
    const processId = getEntityId(proc);
    const partnerTitle = pick(proc, "lendingPartnerTitle", "LendingPartnerTitle");
    const typeTitle = pick(proc, "processTypeTitle", "ProcessTypeTitle");

    const logs = normalizeTimelineLogs(proc?.logs || proc?.Logs || []);
    logs.forEach((log) => {
      const logId = getEntityId(log);
      const status = pick(log, "status", "Status");
      const statusTitle = getLoanStatusLabel(
        status,
        pick(log, "statusTitle", "StatusTitle")
      );
      const actionTitle = pick(log, "actionTitle", "ActionTitle") || "اقدام وام";
      const attachmentUrl = getAttachmentUrl(log);

      let activityType = "lending_log";
      let title = actionTitle;
      if (attachmentUrl) {
        activityType = "lending_attachment";
        title = "پیوست فرآیند وام";
      }
      if (statusTitle) title = `${actionTitle} — ${statusTitle}`;

      const descParts = [
        pick(log, "description", "Description"),
        partnerTitle ? `لندتک: ${partnerTitle}` : "",
        typeTitle ? `نوع: ${typeTitle}` : "",
        pick(log, "trackingCode", "TrackingCode")
          ? `کد رهگیری: ${pick(log, "trackingCode", "TrackingCode")}`
          : "",
      ].filter(Boolean);

      activities.push(
        makeActivity({
          id: `lending-${processId}-log-${logId ?? `n${tie}`}`,
          category: ACTIVITY_CATEGORIES.LENDING,
          activityType,
          title,
          description: descParts.join("\n"),
          occurredAt: pick(log, "createOn", "CreateOn"),
          authorName: pick(log, "createdByFullName", "CreatedByFullName"),
          attachmentUrl,
          chipColor: lendingLogChipColor(status),
          sortTie: tie++,
        })
      );
    });
  });

  return activities;
};

/** فعالیت synthetic از AssignedAt / مسئول فرآیند (بدون event table) */
export const normalizeProcessOwnerActivities = (processes = [], module = "mehrsan") => {
  const isMehrsan = module === "mehrsan";
  const category = isMehrsan ? ACTIVITY_CATEGORIES.MEHRSAN : ACTIVITY_CATEGORIES.LENDING;
  const title = isMehrsan
    ? "مسئول فرآیند مهرسان تغییر کرد"
    : "مسئول فرآیند وام تغییر کرد";
  const activities = [];
  let tie = 0;

  (Array.isArray(processes) ? processes : []).forEach((proc) => {
    const processId = getEntityId(proc);
    const assignedToUserId = pick(proc, "assignedToUserId", "AssignedToUserId");
    const assignedAt = pick(proc, "assignedAt", "AssignedAt");
    const uid = Number(assignedToUserId);
    if (!uid || Number.isNaN(uid) || !assignedAt) return;

    const fullName =
      pick(proc, "assignedToFullName", "AssignedToFullName") || "کارشناس";
    const typeTitle =
      pick(proc, "processTypeTitle", "ProcessTypeTitle") ||
      (isMehrsan ? "مهرسان" : "وام");

    activities.push(
      makeActivity({
        id: `${module}-owner-${processId}-${assignedToUserId}`,
        category,
        activityType: isMehrsan ? "mehrsan_owner_change" : "lending_owner_change",
        title,
        description: `مسئول: ${fullName}\nنوع فرآیند: ${typeTitle}`,
        occurredAt: assignedAt,
        authorName: fullName,
        chipColor: "primary",
        sortTie: tie++,
      })
    );
  });

  return activities;
};

export const mergeAndSortActivities = (...groups) => {
  const merged = groups.flat().filter(Boolean);
  const seen = new Set();
  const unique = [];

  merged.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    unique.push(item);
  });

  return unique.sort((a, b) => {
    const aHasDate = a.occurredAtMs > NO_DATE_MS;
    const bHasDate = b.occurredAtMs > NO_DATE_MS;
    if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
    if (b.occurredAtMs !== a.occurredAtMs) return b.occurredAtMs - a.occurredAtMs;
    if (a.sourcePriority !== b.sourcePriority) {
      return a.sourcePriority - b.sourcePriority;
    }
    return a.sortTie - b.sortTie;
  });
};

export const normalizeInternalNoteActivities = (internalNotes = []) => {
  if (!Array.isArray(internalNotes)) return [];

  const activities = [];
  let tie = 0;

  internalNotes.forEach((item, index) => {
    const noteId = getEntityId(item);
    const noteText = pick(item, "noteText", "NoteText") || "";
    const attachmentUrl = pick(item, "attachmentUrl", "AttachmentUrl");
    const authorName = pick(item, "createdByFullName", "CreatedByFullName");
    const createOn = pick(item, "createOn", "CreateOn");
    const updateOn = pick(item, "updateOn", "UpdateOn");
    const isPinned = Boolean(pick(item, "isPinned", "IsPinned"));
    const createMs = parseTime(createOn);
    const updateMs = parseTime(updateOn);

    activities.push(
      makeActivity({
        id: `internal-note-create-${noteId ?? index}`,
        category: ACTIVITY_CATEGORIES.INTERNAL_NOTE,
        activityType: "internal_note_created",
        title: "ثبت یادداشت داخلی",
        description: noteText,
        occurredAt: createOn,
        authorName,
        attachmentUrl,
        chipColor: "secondary",
        sortTie: tie++,
      })
    );

    if (
      isPinned &&
      updateMs > createMs + PIN_CHANGE_THRESHOLD_MS &&
      updateMs > NO_DATE_MS
    ) {
      activities.push(
        makeActivity({
          id: `internal-note-pin-${noteId ?? index}`,
          category: ACTIVITY_CATEGORIES.INTERNAL_NOTE,
          activityType: "internal_note_pinned",
          title: "سنجاق یادداشت داخلی",
          description: noteText,
          occurredAt: updateOn,
          authorName,
          chipColor: "warning",
          sortTie: tie++,
        })
      );
    } else if (
      !isPinned &&
      updateMs > createMs + PIN_CHANGE_THRESHOLD_MS &&
      updateMs > NO_DATE_MS
    ) {
      activities.push(
        makeActivity({
          id: `internal-note-unpin-${noteId ?? index}`,
          category: ACTIVITY_CATEGORIES.INTERNAL_NOTE,
          activityType: "internal_note_unpinned",
          title: "برداشتن سنجاق یادداشت",
          description: noteText,
          occurredAt: updateOn,
          authorName,
          chipColor: "default",
          sortTie: tie++,
        })
      );
    }
  });

  return activities;
};

const financialTxChipColor = (activityType) => {
  if (activityType === "financial_transaction_approved") return "success";
  if (activityType === "financial_transaction_rejected") return "error";
  if (activityType === "financial_loan_disbursement") return "success";
  return "warning";
};

export const normalizeFinancialActivities = (transactions = []) => {
  if (!Array.isArray(transactions)) return [];

  const activities = [];
  let tie = 0;

  transactions.forEach((tx) => {
    const txId = getEntityId(tx);
    const method = Number(pick(tx, "paymentMethod", "PaymentMethod"));
    const status = Number(pick(tx, "paymentStatus", "PaymentStatus"));
    const amount = formatMoney(pick(tx, "amount", "Amount"));
    const methodTitle = getPaymentMethodLabel(
      method,
      pick(tx, "paymentMethodTitle", "PaymentMethodTitle")
    );
    const statusTitle = getTransactionStatusLabel(
      status,
      pick(tx, "paymentStatusTitle", "PaymentStatusTitle")
    );
    const tracking = pick(tx, "trackingNumber", "TrackingNumber");
    const createOn = pick(tx, "createOn", "CreateOn");
    const approvedAt = pick(tx, "approvedAt", "ApprovedAt");
    const rejectedAt = pick(tx, "rejectedAt", "RejectedAt");
    const rejectReason = pick(tx, "approvalDescription", "ApprovalDescription");

    const descBase = [
      `مبلغ: ${amount}`,
      `روش: ${methodTitle}`,
      statusTitle ? `وضعیت: ${statusTitle}` : "",
      tracking ? `پیگیری: ${tracking}` : "",
      pick(tx, "description", "Description") || "",
    ].filter(Boolean);

    if (method === PAYMENT_METHOD_LOAN) {
      activities.push(
        makeActivity({
          id: `financial-loan-${txId ?? tie}`,
          category: ACTIVITY_CATEGORIES.FINANCIAL,
          activityType: "financial_loan_disbursement",
          title: "ثبت وام",
          description: descBase.join("\n"),
          occurredAt: pick(tx, "paidAt", "PaidAt") || createOn,
          authorName:
            pick(tx, "approvedByFullName", "ApprovedByFullName") ||
            pick(tx, "createBy", "CreateBy"),
          chipColor: financialTxChipColor("financial_loan_disbursement"),
          sortTie: tie++,
        })
      );
      return;
    }

    const registerTitle =
      method === PAYMENT_METHOD_LEGACY ? "ثبت پرداخت (Legacy)" : "ثبت پرداخت";

    activities.push(
      makeActivity({
        id: `financial-register-${txId ?? tie}`,
        category: ACTIVITY_CATEGORIES.FINANCIAL,
        activityType: "financial_transaction_registered",
        title: registerTitle,
        description: descBase.join("\n"),
        occurredAt: createOn,
        authorName: pick(tx, "createBy", "CreateBy"),
        attachmentUrl: pick(tx, "receiptFileUrl", "ReceiptFileUrl"),
        chipColor: financialTxChipColor("financial_transaction_registered"),
        sortTie: tie++,
      })
    );

    if (approvedAt && status === 2) {
      activities.push(
        makeActivity({
          id: `financial-approve-${txId ?? tie}`,
          category: ACTIVITY_CATEGORIES.FINANCIAL,
          activityType: "financial_transaction_approved",
          title: "تایید پرداخت",
          description: descBase.join("\n"),
          occurredAt: approvedAt,
          authorName: pick(tx, "approvedByFullName", "ApprovedByFullName"),
          chipColor: financialTxChipColor("financial_transaction_approved"),
          sortTie: tie++,
        })
      );
    }

    if (status === 5) {
      const rejectDesc = [...descBase];
      if (rejectReason) rejectDesc.push(`دلیل رد: ${rejectReason}`);
      activities.push(
        makeActivity({
          id: `financial-reject-${txId ?? tie}`,
          category: ACTIVITY_CATEGORIES.FINANCIAL,
          activityType: "financial_transaction_rejected",
          title: "رد پرداخت",
          description: rejectDesc.join("\n"),
          occurredAt: rejectedAt || createOn,
          authorName: pick(tx, "rejectedByFullName", "RejectedByFullName"),
          chipColor: financialTxChipColor("financial_transaction_rejected"),
          sortTie: tie++,
        })
      );
    }
  });

  return activities;
};

export const buildActivityFeed = ({
  history = [],
  followUps = [],
  mehrsanProcesses = [],
  lendingProcesses = [],
  internalNotes = [],
  financialTransactions = [],
}) =>
  mergeAndSortActivities(
    normalizeWorkflowActivities(history),
    normalizeFollowUpActivities(followUps),
    normalizeMehrsanActivities(mehrsanProcesses),
    normalizeFinancialActivities(financialTransactions),
    normalizeLendingActivities(lendingProcesses),
    normalizeProcessOwnerActivities(mehrsanProcesses, "mehrsan"),
    normalizeProcessOwnerActivities(lendingProcesses, "lending"),
    normalizeInternalNoteActivities(internalNotes)
  );
